/**
 * 기업 로고
 * ---------------------------------------------------------------------------
 * 여기 한 번 올리면 WORK&Hub · WORK&CRM · WORK&ECO 세 화면의 머리말에 함께
 * 나온다. 제품마다 따로 올리게 두면 바꿀 때마다 세 곳을 돌아야 하고,
 * 한 곳을 빠뜨리면 같은 회사가 제품마다 다른 얼굴을 갖는다.
 *
 * 제품 로고를 지우지 않는다
 *   왼쪽의 제품 로고는 그대로 두고 회사 이름 옆에 나란히 붙인다. 왼쪽은
 *   "지금 쓰는 제품", 오른쪽은 "지금 보는 회사" 로 자리의 뜻이 갈려야
 *   둘이 섞여 보이지 않는다.
 */

import { useEffect, useState, useSyncExternalStore } from 'react';
import { App as AntdApp, Button, Card, Space, Typography, Upload } from 'antd';
import { DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText } from '../../lib/queries';
import {
  getCompanyLogo,
  loadCompanyLogo,
  subscribeCompanyLogo,
} from '../../lib/company-logo';

const { Text, Title } = Typography;

interface LogoMeta {
  contentType: string;
  byteSize: number;
  etag: string;
  updatedAt: string;
}

export function TenantLogoPanel({ canManage }: { canManage: boolean }) {
  const { message } = AntdApp.useApp();
  const url = useSyncExternalStore(subscribeCompanyLogo, getCompanyLogo, () => null);

  const [meta, setMeta] = useState<LogoMeta | null>(null);
  const [maxKb, setMaxKb] = useState(512);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await apiClient.get<{ logo: LogoMeta | null; maxKb: number }>(
        '/tenant/logo/meta',
      );
      setMeta(data.logo);
      setMaxKb(data.maxKb);
    } catch {
      // 상한을 못 받아도 화면은 떠야 한다. 판정은 어차피 서버가 한다.
      setMeta(null);
    }
    await loadCompanyLogo();
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const body = new FormData();
      body.append('file', file);
      await apiClient.put('/tenant/logo', body);
      message.success('기업 로고를 등록했습니다. 머리말에 바로 반영됩니다.');
      await refresh();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await apiClient.delete('/tenant/logo');
      message.success('기업 로고를 삭제했습니다.');
      await refresh();
    } catch (err) {
      message.error(errorText(err as NormalizedError));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card size="small" style={{ marginBottom: 20 }}>
      <Space align="baseline" style={{ marginBottom: 8 }}>
        <Title level={5} style={{ margin: 0 }}>
          기업 로고
        </Title>
        {url && canManage && (
          <Button
            size="small"
            type="link"
            danger
            icon={<DeleteOutlined />}
            loading={busy}
            onClick={() => void remove()}
          >
            삭제
          </Button>
        )}
      </Space>

      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12 }}>
        WORK&amp;Hub · WORK&amp;CRM · WORK&amp;ECO 화면 상단에 함께 표시됩니다. 배경을 지운
        가로형 PNG 가 가장 깨끗합니다. {maxKb}KB 이하의 PNG · JPEG 만 올릴 수 있습니다.
      </Text>

      {url ? (
        <div
          style={{
            border: '1px solid #f0f0f0',
            borderRadius: 6,
            padding: 12,
            marginBottom: 12,
            // 머리말은 어두운 바탕이다. 흰 바탕에서만 확인하면 흰 글자 로고를
            // 올렸을 때 화면에 띄우고 나서야 안 보인다는 것을 알게 된다.
            background: '#001529',
            textAlign: 'center',
          }}
        >
          <img src={url} alt="기업 로고" style={{ height: 28, maxWidth: '100%' }} />
          {meta && (
            <div style={{ marginTop: 8 }}>
              <Text style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
                머리말 미리보기 · {Math.ceil(meta.byteSize / 1024)}KB
              </Text>
            </div>
          )}
        </div>
      ) : (
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 12 }}>
          등록된 로고가 없습니다. 제품 로고만 표시됩니다.
        </Text>
      )}

      {canManage ? (
        <Upload.Dragger
          accept="image/png,image/jpeg"
          maxCount={1}
          showUploadList={false}
          disabled={busy}
          beforeUpload={(file) => {
            void upload(file);
            // 직접 올린다. antd 의 기본 업로드는 인증 헤더를 붙이지 않는다.
            return false;
          }}
        >
          <p className="ant-upload-drag-icon" style={{ margin: 0 }}>
            <InboxOutlined />
          </p>
          <p className="ant-upload-text" style={{ margin: 0 }}>
            {url ? '다시 올리기' : 'PNG · JPEG 이미지를 끌어다 놓거나 눌러서 고르세요'}
          </p>
        </Upload.Dragger>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          로고를 바꾸려면 기업 관리 권한이 필요합니다.
        </Text>
      )}
    </Card>
  );
}
